import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Box,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconLock, IconMail } from '@tabler/icons-react';
import { authApi } from '@/services/api.service';
import { useAuthStore } from '@/stores/authStore';

const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const HomePage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!email || !email.trim()) {
      notifications.show({
        title: 'Erro',
        message: 'Email é obrigatório',
        color: 'red',
      });
      return;
    }

    if (!isValidEmail(email.trim())) {
      notifications.show({
        title: 'Erro',
        message: 'Email inválido',
        color: 'red',
      });
      return;
    }

    if (!password || password.length < 6) {
      notifications.show({
        title: 'Erro',
        message: 'Senha deve ter no mínimo 6 caracteres',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.login({
        email: email.trim(),
        password,
      });

      login(response.user, response.accessToken, response.refreshToken);

      notifications.show({
        title: 'Bem-vindo!',
        message: `Olá, ${response.user.name}`,
        color: 'green',
      });

      navigate('/dashboard');
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);

      const errorMessage =
        error.response?.data?.detail || 'Não foi possível fazer login. Tente novamente.';

      notifications.show({
        title: 'Erro ao entrar',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: 'calc(100vh - 60px - 80px)', // Subtract header and footer
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
      }}
    >
      <Container size="xs">
        <Paper
          shadow="xl"
          p="xl"
          radius="lg"
          style={{
            maxWidth: 400,
          }}
        >
          <Stack gap="lg">
            <div style={{ textAlign: 'center' }}>
              <Title order={1} size="h2" mb="xs">
                Login
              </Title>
              <Text c="dimmed" size="sm">
                Sistema de Apoio à Análise de Requisitos de Integração de Sistemas
              </Text>
            </div>

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  placeholder="seu@email.com"
                  size="md"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  leftSection={<IconMail size={18} />}
                  required
                  autoFocus
                />

                <PasswordInput
                  label="Senha"
                  placeholder="Sua senha"
                  size="md"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  leftSection={<IconLock size={18} />}
                  required
                />

                <Button
                  type="submit"
                  size="md"
                  fullWidth
                  loading={loading}
                  style={{
                    transition: 'transform 0.2s ease',
                  }}
                >
                  Entrar
                </Button>

                <Text size="xs" c="dimmed" ta="center">
                  Esqueceu sua senha?{' '}
                  <Anchor
                    size="xs"
                    c="dimmed"
                    style={{ cursor: 'not-allowed' }}
                    title="Contate o administrador para resetar sua senha"
                  >
                    Contate o administrador
                  </Anchor>
                </Text>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};
